import { Alert, FormControl, Grid, TextField, Typography } from '@mui/material';
import Panel from '../../../components/UI/Panel';
import GenericSelectInput from './UI/GenericSelectInput';
import State from '../../../Models/Locations/state';
import BillingInfo from './BillingInfo';
import MultiSelectInput from './UI/MultiSelectInput';
import DepartmentServeInfo from './Department/DepartmentServeInfo';
import DepartmentServePreferences from './Department/DepartmentServePreferences';
import Dropdown from '../../../Models/Locations/dropdown';
import FollowUpMethods from './FollowUpMethods';
import OtherInfo from './OtherInfo';
import useCheckRecordTypeAssociation from '../../../hooks/Locations/useCheckRecordTypeAssociation';
import { useEffect, useState } from 'react';
import ConfirmationDialog from '../../../components/UI/ConfirmationDialog';
import { Link } from 'react-router-dom';
import useAuth from '../../../hooks/context/useAuth';
import { appActions } from '../../../settings/appConfig';
import WarningIcon from '@mui/icons-material/Warning';
import DetailsPanel from '../../../common/DetailsPanel';

export default function CustodianDetails({
  formik,
  showPanel,
  timeZones,
  recordTypes,
  states,
  paymentMethods,
  serveMethods,
  days,
  orderTypes,
  departmentFileTypes,
  followUpMethods,
  isEdit = false,
}: {
  formik: any;
  showPanel: boolean;
  recordTypes: any[];
  timeZones: any[];
  departmentFileTypes: any[];
  states: State[];
  paymentMethods?: any[];
  serveMethods: any[];
  days: any[];
  orderTypes: any[];
  followUpMethods: Dropdown[]
  isEdit?: boolean
}) {
  const { isAuthorized } = useAuth();
  const { checkRecordType, isAssociated, checkCount } = useCheckRecordTypeAssociation();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [selectedRecordTypeName, setSelectedRecordTypeName] = useState<string | null>(null);
  const [pendingCheck, setPendingCheck] = useState<{ entityId: number; recordTypeId: number } | null>(null);
  const [recordTypeToRemove, setRecordTypeToRemove] = useState<string | null>(null);

  useEffect(() => {
    if (pendingCheck) {
      checkRecordType(pendingCheck.entityId, pendingCheck.recordTypeId);
    }
  }, [pendingCheck]);

  useEffect(() => {
    if (recordTypeToRemove == null || isAssociated === null) return;

    if (isAssociated) {
      const recordTypeObj = recordTypes.find(
        (rt) => String(rt.id) === String(recordTypeToRemove)
      );
      setSelectedRecordTypeName(recordTypeObj?.name ?? "this record type");
      setOpenDialog(true);
    } else {
      const updatedRecordTypes = formik.values.custodianDetails.recordTypes.filter(
        (r: any) => r !== recordTypeToRemove
      );
      formik.setFieldValue("custodianDetails.recordTypes", updatedRecordTypes);
    }

    setPendingCheck(null);
    setRecordTypeToRemove(null);
  }, [isAssociated, checkCount]);

  const togglePressRecordTypes = (recordTypeId: string) => {
    let recordTypes = [...formik.values.custodianDetails.recordTypes];
    const recordIndex = recordTypes.indexOf(recordTypeId);

    if (recordIndex > -1) {
      if (isEdit) {
        setRecordTypeToRemove(recordTypeId);
        setPendingCheck({
          entityId: formik.values.id,
          recordTypeId: Number(recordTypeId),
        });
        return;
      }
      recordTypes = recordTypes.filter((record) => record !== recordTypeId);
    } else {
      recordTypes = [...recordTypes, recordTypeId];
    }

    formik.setFieldValue(`custodianDetails.recordTypes`, recordTypes);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedRecordTypeName(null);
    setPendingCheck(null);
    setRecordTypeToRemove(null);
  };

  const handleRecordTypeChange = (newRecordTypes: any[]) => {
    const prevRecordTypes = formik.values.custodianDetails.recordTypes;

    const removed = prevRecordTypes.filter((r: any) => !newRecordTypes.includes(r));

    if (removed.length > 0 && isEdit) {
      const recordTypeId = removed[0];
      setRecordTypeToRemove(recordTypeId);
      setPendingCheck({
        entityId: formik.values.id,
        recordTypeId: Number(recordTypeId),
      });
      return;
    }

    formik.setFieldValue(`custodianDetails.recordTypes`, newRecordTypes);
  };


  const content = (
    <>
      <Panel headerText="Record Types">
        <Grid spacing={2} mx={1} px={1} container>
          <Grid item xs={4} px={2}>
            <GenericSelectInput
              hasError={
                !!(formik?.touched?.custodianDetails?.recordTypes && formik?.errors?.custodianDetails?.recordTypes)
              }
              errorMsg={
                formik?.touched?.custodianDetails?.recordTypes && formik?.errors?.custodianDetails?.recordTypes
                  ? formik?.errors?.custodianDetails?.recordTypes
                  : ''
              }
              label="Select Record Type(s)"
              name="record-types"
              onBlur={() => {
                formik?.setFieldTouched(`custodianDetails.recordTypes`, true);
              }}
              onChange={(r: any) => { handleRecordTypeChange(r) }}
              options={recordTypes}
              value={formik?.values?.custodianDetails?.recordTypes}
              multiple
              required={true}
            />
          </Grid>
          <Grid item xs={7} px={2}>
            <FormControl fullWidth>
              <TextField
                name={'recordTypeNotes'}
                label="Notes"
                placeholder="Notes"
                multiline
                variant="outlined"
                inputMode={'text'}
                inputProps={{ maxLength: 500 }}
                value={formik?.values?.custodianDetails?.recordTypeNotes}
                onChange={(e: any) => {
                  formik?.setFieldValue(`custodianDetails.recordTypeNotes`, e.target.value);
                }}
                error={
                  !!(formik?.touched?.custodianDetails?.recordTypeNotes &&
                    formik?.errors?.custodianDetails?.recordTypeNotes)
                }
                helperText={
                  formik?.touched?.custodianDetails?.recordTypeNotes &&
                    formik?.errors?.custodianDetails?.recordTypeNotes
                    ? formik?.errors.custodianDetails?.recordTypeNotes
                    : ''
                }
                onBlur={() => {
                  formik?.setFieldTouched(`custodianDetails.recordTypeNotes`, true);
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} px={2}>
            <MultiSelectInput
              options={recordTypes}
              errorMsg=''
              togglePress={togglePressRecordTypes}
              values={formik?.values?.custodianDetails?.recordTypes}
              showOnlySelected />
          </Grid>
        </Grid>
      </Panel>
      <Panel headerText="Order Types">
        <Grid spacing={2} mx={1} px={1} container>
          <Grid item xs={4} px={2}>
            <GenericSelectInput
              hasError={
                !!(formik?.touched?.custodianDetails?.orderTypeId && formik?.errors?.custodianDetails?.orderTypeId)
              }
              errorMsg={
                formik?.touched?.custodianDetails?.orderTypeId && formik?.errors?.custodianDetails?.orderTypeId
                  ? formik?.errors?.custodianDetails?.orderTypeId
                  : ''
              }
              label="Select Order Type(s)"
              name="order-types"
              onBlur={() => {
                formik?.setFieldTouched(`custodianDetails.orderTypeId`, true);
              }}
              onChange={(r) => {
                formik.setFieldValue(
                  `custodianDetails.orderTypeId`,
                  r
                );
              }}
              options={orderTypes}
              value={formik?.values?.custodianDetails?.orderTypeId}
              required={true}
            />
          </Grid>
          <Grid item xs={7} px={2}>
            <FormControl fullWidth>
              <TextField
                name={'orderTypeNotes'}
                label="Notes"
                placeholder="Notes"
                multiline
                variant="outlined"
                inputMode={'text'}
                inputProps={{ maxLength: 500 }}
                value={formik?.values?.custodianDetails?.orderTypeNotes}
                onChange={(e: any) => {
                  formik?.setFieldValue(`custodianDetails.orderTypeNotes`, e.target.value);
                }}
                error={
                  !!(formik?.touched?.custodianDetails?.orderTypeNotes &&
                    formik?.errors?.custodianDetails?.orderTypeNotes)
                }
                helperText={
                  formik?.touched?.custodianDetails?.orderTypeNotes &&
                    formik?.errors?.custodianDetails?.orderTypeNotes
                    ? formik?.errors.custodianDetails?.orderTypeNotes
                    : ''
                }
                onBlur={() => {
                  formik?.setFieldTouched(`custodianDetails.orderTypeNotes`, true);
                }}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Panel>
      <FollowUpMethods
        formik={formik}
        followUpMethods={followUpMethods}
      />
      <OtherInfo formik={formik} />
      <DepartmentServeInfo
        form={formik}
        timeZones={timeZones}
        serveMethods={serveMethods}
        days={days}
      />
      <DepartmentServePreferences
        departmentFileTypes={departmentFileTypes}
        form={formik}
      />
      <BillingInfo
        form={formik}
        states={states}
        paymentMethods={paymentMethods} />

      {openDialog &&
        <ConfirmationDialog
          open={openDialog}
          onClose={() => handleDialogClose()}
          title='Warning'
          description={<>
            You cannot remove <b>{selectedRecordTypeName}</b> because this record type has existing associations with one or more records.
            <br />
            {formik?.values?.isFacility && isAuthorized(appActions.editAssociations) ? (
              <span>
                If you wish to manage the associations, please navigate to{' '}
                <Link to={`/associations/edit/${formik?.values?.id}`} style={{ color: '#f5831f' }}>
                  Manage Associations
                </Link>
              </span>
            ) : (
              (formik?.values?.isFacility && <>
                <br />
                <Alert severity="warning" icon={<WarningIcon fontSize="small" />} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    Please note, you do not have the necessary permissions to manage associations.
                  </Typography>
                </Alert>
              </>)
            )}

          </>}
          primaryButtonText='Ok'
          onPrimaryAction={() => handleDialogClose()}
        />
      }
    </>
  );
  return (
    <DetailsPanel title="Custodian Details" content={content} showPanel={showPanel} />
  );
}

CustodianDetails.defaultProps = {
  showPanel: true,
};
